import type { CSSProperties, ReactNode } from 'react';

import type { CSSHeight, CSSWidth } from 'types';
import { cnm } from 'utils/classNames';
import { range } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { ENTITIES } from 'utils/string';

interface Props {
	/** Children (probably SkeletonItem) */
	children: ReactNode;

	/** Additional class names */
	className?: string;
}

/** Container for loading skeleton items. */
export function LoadingSkeleton({ children, className }: Props) {
	const { t } = useI18n();

	return (
		<div
			role="progressbar"
			aria-busy="true"
			aria-label={t('general_loading_text')}
			className={className}
		>
			{children}
		</div>
	);
}
LoadingSkeleton.displayName = 'LoadingSkeleton';

interface BaseItemProps {
	className?: string;
	width?: CSSWidth;
}
interface ItemPropsWithCSSHeight extends BaseItemProps {
	height?: CSSHeight;
	lines?: never;
}
interface ItemPropsWithTextHeight extends BaseItemProps {
	/** Make the height the same as a line of text */
	height:
		| 'text'
		| 'text-h1'
		| 'text-h2'
		| 'text-h3'
		| 'text-h4'
		| 'text-h5'
		| 'text-h6'
		| 'text-lg'
		| 'text-sm'
		| 'text-xs';
	lines?: number;
}

type ItemProps = ItemPropsWithCSSHeight | ItemPropsWithTextHeight;

export function LoadingSkeletonItem({
	className,
	height,
	lines = 1,
	width,
}: ItemProps) {
	const isTextHeight = height?.startsWith('text');

	const style: CSSProperties = {};
	if (height && !isTextHeight) {
		style.height = height;
	}
	if (width) {
		style.width = width;
	}

	const content = isTextHeight
		? range(lines)
				.map(() => ENTITIES.noBreakSpace)
				.join('\n')
		: null;

	return (
		<div
			className={cnm(
				'animate-shimmer rounded-xs text-transparent',
				'bg-shimmer bg-size-[200px_100%] bg-no-repeat',
				'bg-linear-to-r from-shimmer via-shimmerHighlight to-shimmer',
				// Don't use the shimmer gradient in forced colors mode, the page
				// background color is unknown so the shimmer color can be a
				// very bad match.
				'forced-color-adjust-none forced-colors:animate-pulse forced-colors:bg-[CanvasText] forced-colors:bg-none forced-colors:opacity-20',
				!isTextHeight && 'h-full leading-none',
				isTextHeight && [
					lines > 1 && 'whitespace-pre-line',
					// 'text' is just the current text style
					height !== 'text' && height,
				],
				className,
			)}
			style={style}
		>
			{content}
		</div>
	);
}
LoadingSkeletonItem.displayName = 'LoadingSkeletonItem';
